import {
  useRef,
  useState,
  useEffect,
} from 'react';
import ReactMarkdown from 'react-markdown';

import {
  getOuterHeight,
} from 'web-utils';

import {
  Text,
} from 'web-styled';

import {
  Icon,
} from 'web-components';

import DocumentLink from './DocumentLink';
import * as S from './styled';

const getPreferredHeight = (nodes: NodeListOf<ChildNode>, maxHeight: number) => {
  let sum = 0;
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    const nodeHeight = node instanceof HTMLElement ? getOuterHeight(node) : 10;
    if (sum + nodeHeight < maxHeight) {
      sum += nodeHeight;
    } else {
      const lineHeight = parseInt(getComputedStyle(document.body).lineHeight, 10);
      const line = Math.floor((maxHeight - sum) / lineHeight);
      sum += line * lineHeight;
      break;
    }
  }

  return sum;
};

const urlTransform = (href: string): string => {
  if (href.indexOf('document:') !== -1) {
    return `/documents/${href.split(':')[1]}`;
  }

  return '';
};

type Props = {
  height?: string,
  source?: string,
  maxHeight?: number,
  placeholder?: string | null,
};

const MarkdownRenderer = ({
  height = 'auto',
  source = '',
  maxHeight,
  placeholder,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [collapseHeight, setCollapseHeight] = useState(maxHeight);
  const [useCollapse, canUseCollapse] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (ref.current === null) return;
    if (ref.current && maxHeight && ref.current.offsetHeight > maxHeight) {
      const nodes = ref.current.childNodes[0].childNodes;
      setCollapseHeight(
        getPreferredHeight(nodes, maxHeight),
      );
      canUseCollapse(true);
    }
  }, [height, source]);

  if (source) {
    return (
      <S.Renderer
        ref={ref}
        useCollapse={useCollapse}
        maxHeight={collapseHeight}
        isOpen={isOpen}
      >
        <div>
          <ReactMarkdown
            urlTransform={urlTransform}
            components={{
              a: ({ href, children }) => (
                <DocumentLink href={href}>
                  {children}
                </DocumentLink >
              )
            }}
          >
            {source}
          </ReactMarkdown>
        </div>
        {useCollapse && (
          <S.More
            type="button"
            isOpen={isOpen}
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon
              type={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}
            />
            {isOpen ? 'See Less' : 'See More'}
          </S.More>
        )}
      </S.Renderer>
    );
  }

  return placeholder ? (
    <Text color="#c1c1c1">
      {placeholder}
    </Text>
  ) : null;
};

export default MarkdownRenderer;
